import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { pl as plLocale } from 'date-fns/locale';

const dfl = () => (String(i18n.language || 'en').startsWith('pl') ? { locale: plLocale } : undefined);
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { format, eachDayOfInterval, getWeek, isWeekend } from 'date-fns';

// Company color palette (ARGB format for ExcelJS)
// Based on company logo colors: grey #4d4d4d and red #aa0000
const COLORS = {
  // Primary colors from logo
  grey: 'FF4D4D4D',
  red: 'FFAA0000',
  
  // Headers and structure
  header: 'FFE8E8E8',           // Light grey
  weekHeaderOdd: 'FFD9D9D9',    // Medium grey
  weekHeaderEven: 'FFC4C4C4',   // Darker grey
  
  // Calendar colors
  sunday: 'FFFFEBEB',           // Light red tint
  saturday: 'FFFFF0F0',         // Very light red tint
  holiday: 'FFFFCCCC',          // Red tint for holidays
  holidayRow: 'FFFFEAEA',       // Light red for holiday info row
  
  // Status colors
  available: 'FFE8F5E9',        // Light green
  unavailable: 'FFFFCDD2',      // Light red
  nonWorking: 'FFF5F5F5',       // Very light grey
  
  // Other
  total: 'FFE0E0E0',            // Grey for totals
  white: 'FFFFFFFF',
  spacer: 'FFECEFF1',
  employee: 'FFF9FAFB',
  
  // Text colors
  titleColor: 'FF4D4D4D',       // Grey for titles
  accentColor: 'FFAA0000',      // Red for accents
};

// Border colors
const BORDER_COLORS = {
  thin: 'FF9E9E9E',     // Medium grey
  thick: 'FF4D4D4D',    // Dark grey (company grey)
};

// Format date for display
const formatDateDDMMYYYY = (date) => format(new Date(date), 'yyyy.MM.dd');
const formatDateFull = (date) => format(new Date(date), 'yyyy.MM.dd');

// Get week number
const getWeekNumber = (date) => getWeek(new Date(date), { weekStartsOn: 1 });

// Add logo embedded directly in the Excel file (no external URL, no security warnings)
const addLogoEmbedded = async (workbook, worksheet, companyInfo, rowNum, colNum) => {
  if (!companyInfo) return false;
  
  const logoUrl = companyInfo.header_logo_file || companyInfo.logo_file;
  if (!logoUrl) return false;
  
  try {
    const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
    const fullLogoUrl = logoUrl.startsWith('http') ? logoUrl : 
      logoUrl.startsWith('/api/') ? `${API_BASE}${logoUrl}` : 
      `${API_BASE}/api/files/logo/${logoUrl}`;
    
    // Fetch the image and convert to base64
    const logoResponse = await fetch(fullLogoUrl);
    if (!logoResponse.ok) return false;
    
    const logoBlob = await logoResponse.blob();
    const logoArrayBuffer = await logoBlob.arrayBuffer();
    const logoBase64 = btoa(
      new Uint8Array(logoArrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
    );
    
    const extension = logoUrl.split('.').pop().toLowerCase();
    const imageExtension = extension === 'png' ? 'png' : 'jpeg';
    
    // Add image to workbook
    const imageId = workbook.addImage({
      base64: logoBase64,
      extension: imageExtension,
    });
    
    // Position image anchored to the cell using 'oneCell' mode
    // This makes the image move and resize with the cell
    worksheet.addImage(imageId, {
      tl: { col: colNum - 1, row: rowNum - 1 },
      ext: { width: 130, height: 30 },
      editAs: 'oneCell'
    });
    
    return true;
  } catch (error) {
    console.warn('Could not embed logo in Excel:', error);
    return false;
  }
};

// Style company info area with white background and no internal borders
const styleCompanyInfoArea = (worksheet, startRow, endRow, startCol, endCol) => {
  for (let r = startRow; r <= endRow; r++) {
    for (let c = startCol; c <= endCol; c++) {
      const cell = worksheet.getCell(r, c);
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: COLORS.white }
      };
      // No internal borders - only outer borders if at edge
      cell.border = {
        top: r === startRow ? { style: 'thin', color: { argb: BORDER_COLORS.thin } } : undefined,
        bottom: r === endRow ? { style: 'medium', color: { argb: BORDER_COLORS.thick } } : undefined,
        left: c === startCol ? { style: 'thin', color: { argb: BORDER_COLORS.thin } } : undefined,
        right: c === endCol ? { style: 'thin', color: { argb: BORDER_COLORS.thin } } : undefined,
      };
    }
  }
};

// Apply cell style with optional thick borders for week separation
const applyStyle = (cell, bgColor, options = {}) => {
  if (bgColor) {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: bgColor }
    };
  }
  
  cell.font = {
    bold: options.bold || false,
    size: options.fontSize || 10,
    color: options.fontColor ? { argb: options.fontColor } : { argb: COLORS.grey }
  };
  
  cell.alignment = {
    horizontal: options.align || 'center',
    vertical: 'middle',
    wrapText: options.wrap || false
  };
  
  const thinBorderColor = { argb: BORDER_COLORS.thin };
  const thickBorderColor = { argb: BORDER_COLORS.thick };
  
  cell.border = {
    top: { 
      style: options.thickTop ? 'medium' : (options.thickBorder ? 'medium' : 'thin'), 
      color: options.thickTop || options.thickBorder ? thickBorderColor : thinBorderColor 
    },
    bottom: { 
      style: options.thickBottom ? 'medium' : (options.thickBorder ? 'medium' : 'thin'), 
      color: options.thickBottom || options.thickBorder ? thickBorderColor : thinBorderColor 
    },
    left: { 
      style: options.thickLeft ? 'medium' : 'thin', 
      color: options.thickLeft ? thickBorderColor : thinBorderColor 
    },
    right: { 
      style: options.thickRight ? 'medium' : 'thin', 
      color: options.thickRight ? thickBorderColor : thinBorderColor 
    }
  };
};

// Add legend to worksheet
const addLegend = (worksheet, startRow, legendItems) => {
  worksheet.addRow([]);
  
  let row = worksheet.addRow(['Legend:']);
  row.height = 20;
  row.getCell(1).font = { bold: true, size: 11, color: { argb: COLORS.grey } };
  
  for (let i = 0; i < legendItems.length; i += 2) {
    const rowData = [];
    rowData.push('');
    rowData.push(legendItems[i].label);
    
    if (legendItems[i + 1]) {
      rowData.push('');
      rowData.push('');
      rowData.push(legendItems[i + 1].label);
    }
    
    row = worksheet.addRow(rowData);
    row.height = 18;
    
    const cell1 = row.getCell(1);
    cell1.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: legendItems[i].color }
    };
    cell1.border = {
      top: { style: 'thin', color: { argb: BORDER_COLORS.thin } },
      bottom: { style: 'thin', color: { argb: BORDER_COLORS.thin } },
      left: { style: 'thin', color: { argb: BORDER_COLORS.thin } },
      right: { style: 'thin', color: { argb: BORDER_COLORS.thin } }
    };
    row.getCell(2).font = { size: 9, color: { argb: COLORS.grey } };
    row.getCell(2).alignment = { horizontal: 'left' };
    
    if (legendItems[i + 1]) {
      const cell2 = row.getCell(4);
      cell2.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: legendItems[i + 1].color }
      };
      cell2.border = {
        top: { style: 'thin', color: { argb: BORDER_COLORS.thin } },
        bottom: { style: 'thin', color: { argb: BORDER_COLORS.thin } },
        left: { style: 'thin', color: { argb: BORDER_COLORS.thin } },
        right: { style: 'thin', color: { argb: BORDER_COLORS.thin } }
      };
      row.getCell(5).font = { size: 9, color: { argb: COLORS.grey } };
      row.getCell(5).alignment = { horizontal: 'left' };
    }
  }
};

// Export Team Availability to Excel with date range
export const exportTeamAvailability = async ({
  employees,
  availability,
  holidays,
  startDate,
  endDate,
  getAvailabilityTypeInfo,
  companyInfo
}) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(i18n.t('docx.teamAvailability'));
  
  const dates = eachDayOfInterval({ start: new Date(startDate), end: new Date(endDate) });
  const activeEmployees = employees.filter(e => e.status === 'active' || e.is_active !== false);
  
  // Group dates by week
  const weekGroups = [];
  let currentWeek = null;
  dates.forEach(date => {
    const weekNum = getWeekNumber(date);
    if (!currentWeek || currentWeek.weekNum !== weekNum) {
      currentWeek = { weekNum, days: [], startIdx: dates.indexOf(date) };
      weekGroups.push(currentWeek);
    }
    currentWeek.days.push(date);
  });

  // Check if date is a holiday
  const isHoliday = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return holidays.find(h => h.date === dateStr);
  };

  // Get employee availability for a date
  const getEmployeeAvailability = (employeeId, date) => {
    return availability.filter(a => {
      const start = new Date(a.start_date);
      const end = new Date(a.end_date);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      const checkDate = new Date(date);
      checkDate.setHours(12, 0, 0, 0);
      return a.employee_id === employeeId && checkDate >= start && checkDate <= end;
    });
  };

  // Determine if a date is the first/last day of its week
  const isFirstDayOfWeek = (dateIdx) => {
    for (const week of weekGroups) {
      if (dateIdx === week.startIdx) return true;
    }
    return false;
  };

  const isLastDayOfWeek = (dateIdx) => {
    for (const week of weekGroups) {
      const lastIdx = week.startIdx + week.days.length - 1;
      if (dateIdx === lastIdx) return true;
    }
    return false;
  };

  // Set column widths
  worksheet.columns = [
    { width: 22 }, // Employee
    ...dates.map(() => ({ width: 12 }))
  ];

  // Row 1: Title
  let row = worksheet.addRow([i18n.t('docx.teamAvailabilityOverview')]);
  row.height = 25;
  const titleCell = row.getCell(1);
  titleCell.font = { bold: true, size: 14, color: { argb: COLORS.grey } };
  titleCell.alignment = { horizontal: 'left', vertical: 'middle' };
  
  // Row 2: Period
  const startStr = formatDateFull(new Date(startDate));
  const endStr = formatDateFull(new Date(endDate));
  row = worksheet.addRow([`Period: ${startStr} - ${endStr}`]);
  row.height = 18;
  row.getCell(1).font = { size: 10, color: { argb: COLORS.grey } };
  row.getCell(1).alignment = { horizontal: 'left' };
  
  // Row 3: Empty
  worksheet.addRow([]);

  // Row 4: Week numbers header with alternating colors
  row = worksheet.addRow([i18n.t('docx.employee'), ...dates.map(() => '')]);
  row.height = 25;
  applyStyle(row.getCell(1), COLORS.weekHeaderOdd, { bold: true, thickBorder: true, fontColor: COLORS.grey });
  
  let colIdx = 2;
  weekGroups.forEach((week, weekIdx) => {
    const startCol = colIdx;
    const weekColor = weekIdx % 2 === 0 ? COLORS.weekHeaderOdd : COLORS.weekHeaderEven;
    week.days.forEach((_, dayIdx) => {
      const cell = row.getCell(colIdx);
      if (dayIdx === 0) {
        cell.value = `Week ${week.weekNum}`;
      }
      const isFirstDay = dayIdx === 0;
      const isLastDay = dayIdx === week.days.length - 1;
      applyStyle(cell, weekColor, { bold: true, thickBorder: true, thickLeft: isFirstDay, thickRight: isLastDay });
      colIdx++;
    });
    if (week.days.length > 1) {
      worksheet.mergeCells(4, startCol, 4, startCol + week.days.length - 1);
    }
  });
  
  // Row 5: Day names
  row = worksheet.addRow(['', ...dates.map(d => format(d, 'EEE', dfl()))]);
  row.height = 20;
  applyStyle(row.getCell(1), COLORS.white, { bold: true });
  dates.forEach((date, idx) => {
    const cell = row.getCell(idx + 2);
    const isSunday = date.getDay() === 0;
    const isSaturday = date.getDay() === 6;
    const bgColor = isSunday ? COLORS.sunday : (isSaturday ? COLORS.saturday : COLORS.header);
    const isFirst = isFirstDayOfWeek(idx);
    const isLast = isLastDayOfWeek(idx);
    applyStyle(cell, bgColor, { bold: true, thickLeft: isFirst, thickRight: isLast });
  });
  
  // Row 6: Dates
  row = worksheet.addRow(['', ...dates.map(d => formatDateDDMMYYYY(d))]);
  row.height = 20;
  applyStyle(row.getCell(1), COLORS.white, { bold: true });
  dates.forEach((date, idx) => {
    const cell = row.getCell(idx + 2);
    const isSunday = date.getDay() === 0;
    const isSaturday = date.getDay() === 6;
    const bgColor = isSunday ? COLORS.sunday : (isSaturday ? COLORS.saturday : COLORS.header);
    const isFirst = isFirstDayOfWeek(idx);
    const isLast = isLastDayOfWeek(idx);
    applyStyle(cell, bgColor, { bold: true, thickLeft: isFirst, thickRight: isLast });
  });

  // Row 7: Holiday row
  const holidayRowData = [''];
  dates.forEach(date => {
    const holiday = isHoliday(date);
    if (holiday) {
      holidayRowData.push(`🏴 ${holiday.name}`);
    } else {
      holidayRowData.push('');
    }
  });
  row = worksheet.addRow(holidayRowData);
  row.height = 22;
  applyStyle(row.getCell(1), COLORS.white, { bold: true, thickBottom: true });
  dates.forEach((date, idx) => {
    const cell = row.getCell(idx + 2);
    const isFirst = isFirstDayOfWeek(idx);
    const isLast = isLastDayOfWeek(idx);
    applyStyle(cell, COLORS.holidayRow, { fontSize: 8, wrap: true, thickLeft: isFirst, thickRight: isLast, thickBottom: true });
  });

  // Add company info to cells A5:A7 with white background and no internal borders
  styleCompanyInfoArea(worksheet, 5, 7, 1, 1);
  
  if (companyInfo) {
    // Row 5: Logo embedded directly in the file
    await addLogoEmbedded(workbook, worksheet, companyInfo, 5, 1);
    worksheet.getRow(5).height = 35;
    
    // Row 6: Company name
    if (companyInfo.name) {
      const nameCell = worksheet.getCell(6, 1);
      nameCell.value = companyInfo.name;
      nameCell.font = { bold: true, size: 14, color: { argb: COLORS.grey } };
      nameCell.alignment = { horizontal: 'left', vertical: 'middle' };
    }
    
    // Row 7: Address
    if (companyInfo.address) {
      const addrCell = worksheet.getCell(7, 1);
      addrCell.value = companyInfo.address;
      addrCell.font = { size: 11, color: { argb: COLORS.grey } };
      addrCell.alignment = { horizontal: 'left', vertical: 'middle' };
    }
  }

  // Employee rows
  activeEmployees.forEach(emp => {
    const rowData = [emp.name];
    dates.forEach(date => {
      const empAvail = getEmployeeAvailability(emp.id, date);
      
      if (empAvail.length > 0) {
        rowData.push(empAvail.map(a => getAvailabilityTypeInfo(a.availability_type).shortLabel).join(', '));
      } else {
        rowData.push('✓');
      }
    });
    
    row = worksheet.addRow(rowData);
    row.height = 22;
    
    applyStyle(row.getCell(1), COLORS.employee, { align: 'left', bold: true });
    
    dates.forEach((date, idx) => {
      const cell = row.getCell(idx + 2);
      const empAvail = getEmployeeAvailability(emp.id, date);
      const holiday = isHoliday(date);
      const isSunday = date.getDay() === 0;
      const isSaturday = date.getDay() === 6;
      const isFirst = isFirstDayOfWeek(idx);
      const isLast = isLastDayOfWeek(idx);
      
      let bgColor = COLORS.white;
      if (empAvail.length > 0) bgColor = COLORS.unavailable;
      else if (holiday) bgColor = COLORS.holiday;
      else if (isSunday) bgColor = COLORS.sunday;
      else if (isSaturday) bgColor = COLORS.saturday;
      else bgColor = COLORS.available;
      
      applyStyle(cell, bgColor, { thickLeft: isFirst, thickRight: isLast });
    });
  });

  // Freeze panes
  worksheet.views = [
    { state: 'frozen', xSplit: 1, ySplit: 7, topLeftCell: 'B8', activeCell: 'B8' }
  ];

  // Add legend
  addLegend(worksheet, worksheet.rowCount + 2, [
    { color: COLORS.available, label: i18n.t('auto.lbl.available') },
    { color: COLORS.unavailable, label: i18n.t('auto.lbl.leaveUnavailable') },
    { color: COLORS.saturday, label: i18n.t('auto.lbl.saturday') },
    { color: COLORS.sunday, label: i18n.t('auto.lbl.sunday') },
    { color: COLORS.holiday, label: i18n.t('auto.lbl.publicHoliday') },
    { color: COLORS.holidayRow, label: i18n.t('auto.lbl.holidayInfoRow') },
  ]);

  // Generate file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const fileStartStr = format(new Date(startDate), 'yyyy-MM-dd');
  const fileEndStr = format(new Date(endDate), 'yyyy-MM-dd');
  saveAs(blob, `Team_Availability_${fileStartStr}_to_${fileEndStr}.xlsx`);
  
  return true;
};

// Export Project Plan to Excel with date range
export const exportProjectPlan = async ({
  plan,
  planEntries,
  planEmployees,
  polandHolidays,
  projectHolidays,
  customNonWorkingDays,
  startDate,
  endDate,
  employees,
  companyInfo
}) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(i18n.t('docx.projectPlan'));
  
  const planStart = startDate ? new Date(startDate) : new Date(plan.start_date);
  const planEnd = endDate ? new Date(endDate) : new Date(plan.end_date);
  const dates = eachDayOfInterval({ start: planStart, end: planEnd });
  
  // Group dates by week
  const weekGroups = [];
  let currentWeek = null;
  dates.forEach((date, idx) => {
    const weekNum = getWeekNumber(date);
    if (!currentWeek || currentWeek.weekNum !== weekNum) {
      currentWeek = { weekNum, days: [], startIdx: idx };
      weekGroups.push(currentWeek);
    }
    currentWeek.days.push(date);
  });

  // Check if date is a holiday
  const isHoliday = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const projHoliday = projectHolidays.find(h => h.date === dateStr);
    if (projHoliday) return { holiday: projHoliday, type: 'project' };
    const plHoliday = polandHolidays.find(h => h.date === dateStr);
    if (plHoliday) return { holiday: plHoliday, type: 'poland' };
    const customHoliday = customNonWorkingDays.find(d => {
      const customDateStr = typeof d === 'string' ? d.split('T')[0] : format(new Date(d), 'yyyy-MM-dd');
      return customDateStr === dateStr;
    });
    if (customHoliday) return { holiday: { name: i18n.t('docx.custom') }, type: 'custom' };
    return null;
  };

  // Get entry for employee on date
  const getEntry = (employeeId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return planEntries.find(e => 
      e.employee_id === employeeId && 
      (e.date.split('T')[0] === dateStr || format(new Date(e.date), 'yyyy-MM-dd') === dateStr)
    );
  };

  const isFirstDayOfWeek = (dateIdx) => {
    for (const week of weekGroups) {
      if (dateIdx === week.startIdx) return true;
    }
    return false;
  };

  const isLastDayOfWeek = (dateIdx) => {
    for (const week of weekGroups) {
      const lastIdx = week.startIdx + week.days.length - 1;
      if (dateIdx === lastIdx) return true;
    }
    return false;
  };

  // Set column widths
  const colWidths = [{ width: 22 }, { width: 14 }]; // Employee, Role
  dates.forEach(() => {
    colWidths.push({ width: 6 }); // Work
    colWidths.push({ width: 6 }); // Travel
  });
  colWidths.push({ width: 9 }); // Total Work
  colWidths.push({ width: 9 }); // Total Travel
  worksheet.columns = colWidths;

  // Row 1: Plan name (title)
  let row = worksheet.addRow([`Project Plan: ${plan.name || plan.project_name}`]);
  row.height = 25;
  const titleCell = row.getCell(1);
  titleCell.font = { bold: true, size: 14, color: { argb: COLORS.grey } };
  titleCell.alignment = { horizontal: 'left', vertical: 'middle' };
  worksheet.mergeCells(1, 1, 1, 2);
  
  // Row 2: Period
  row = worksheet.addRow([`Period: ${formatDateFull(planStart)} - ${formatDateFull(planEnd)}`]);
  row.height = 18;
  row.getCell(1).font = { size: 10, color: { argb: COLORS.grey } };
  row.getCell(1).alignment = { horizontal: 'left' };
  worksheet.mergeCells(2, 1, 2, 2);
  
  // Row 3: Empty
  worksheet.addRow([]);

  // Row 4: Week numbers header
  const weekRow = [i18n.t('docx.employee'), i18n.t('docx.role')];
  weekGroups.forEach(week => {
    week.days.forEach(() => {
      weekRow.push('');
      weekRow.push('');
    });
  });
  weekRow.push(i18n.t('docx.total'), '');
  row = worksheet.addRow(weekRow);
  row.height = 22;
  
  applyStyle(row.getCell(1), COLORS.weekHeaderOdd, { bold: true, thickBorder: true });
  applyStyle(row.getCell(2), COLORS.weekHeaderOdd, { bold: true, thickBorder: true });
  
  let colIdx = 3;
  weekGroups.forEach((week, weekIdx) => {
    const startCol = colIdx;
    const weekColor = weekIdx % 2 === 0 ? COLORS.weekHeaderOdd : COLORS.weekHeaderEven;
    week.days.forEach((_, dayIdx) => {
      if (dayIdx === 0) {
        row.getCell(colIdx).value = `Week ${week.weekNum}`;
      }
      const isFirstDay = dayIdx === 0;
      const isLastDay = dayIdx === week.days.length - 1;
      applyStyle(row.getCell(colIdx), weekColor, { bold: true, thickBorder: true, thickLeft: isFirstDay });
      applyStyle(row.getCell(colIdx + 1), weekColor, { bold: true, thickBorder: true, thickRight: isLastDay });
      colIdx += 2;
    });
    if (week.days.length * 2 > 1) {
      worksheet.mergeCells(4, startCol, 4, startCol + (week.days.length * 2) - 1);
    }
  });
  
  applyStyle(row.getCell(colIdx), COLORS.total, { bold: true, thickBorder: true, thickLeft: true });
  applyStyle(row.getCell(colIdx + 1), COLORS.total, { bold: true, thickBorder: true });
  worksheet.mergeCells(4, colIdx, 4, colIdx + 1);
  
  // Row 5: Day names
  const dayNameRow = ['', ''];
  dates.forEach(d => {
    dayNameRow.push(format(d, 'EEE', dfl()));
    dayNameRow.push('');
  });
  dayNameRow.push(i18n.t('docx.work'), i18n.t('docx.travel'));
  row = worksheet.addRow(dayNameRow);
  row.height = 20;
  
  applyStyle(row.getCell(1), COLORS.white);
  applyStyle(row.getCell(2), COLORS.white);
  
  colIdx = 3;
  dates.forEach((date, dateIdx) => {
    const isSunday = date.getDay() === 0;
    const isSaturday = date.getDay() === 6;
    const holidayInfo = isHoliday(date);
    let bgColor = COLORS.header;
    if (holidayInfo) bgColor = COLORS.holiday;
    else if (isSunday) bgColor = COLORS.sunday;
    else if (isSaturday) bgColor = COLORS.saturday;
    
    const isFirst = isFirstDayOfWeek(dateIdx);
    const isLast = isLastDayOfWeek(dateIdx);
    applyStyle(row.getCell(colIdx), bgColor, { bold: true, thickLeft: isFirst });
    applyStyle(row.getCell(colIdx + 1), bgColor, { thickRight: isLast });
    worksheet.mergeCells(5, colIdx, 5, colIdx + 1);
    colIdx += 2;
  });
  applyStyle(row.getCell(colIdx), COLORS.total, { bold: true, thickLeft: true });
  applyStyle(row.getCell(colIdx + 1), COLORS.total, { bold: true });
  
  // Row 6: Dates
  const dateRow = ['', ''];
  dates.forEach(d => {
    dateRow.push(formatDateDDMMYYYY(d));
    dateRow.push('');
  });
  dateRow.push(i18n.t('docx.hours'), i18n.t('docx.hours'));
  row = worksheet.addRow(dateRow);
  row.height = 20;
  
  applyStyle(row.getCell(1), COLORS.white);
  applyStyle(row.getCell(2), COLORS.white);
  
  colIdx = 3;
  dates.forEach((date, dateIdx) => {
    const isSunday = date.getDay() === 0;
    const isSaturday = date.getDay() === 6;
    const holidayInfo = isHoliday(date);
    let bgColor = COLORS.header;
    if (holidayInfo) bgColor = COLORS.holiday;
    else if (isSunday) bgColor = COLORS.sunday;
    else if (isSaturday) bgColor = COLORS.saturday;
    
    const isFirst = isFirstDayOfWeek(dateIdx);
    const isLast = isLastDayOfWeek(dateIdx);
    applyStyle(row.getCell(colIdx), bgColor, { bold: true, thickLeft: isFirst });
    applyStyle(row.getCell(colIdx + 1), bgColor, { thickRight: isLast });
    worksheet.mergeCells(6, colIdx, 6, colIdx + 1);
    colIdx += 2;
  });
  applyStyle(row.getCell(colIdx), COLORS.total, { bold: true, thickLeft: true });
  applyStyle(row.getCell(colIdx + 1), COLORS.total, { bold: true });
  
  // Row 7: W/T headers
  const wtRow = ['', ''];
  dates.forEach(() => {
    wtRow.push('W');
    wtRow.push('T');
  });
  wtRow.push('', '');
  row = worksheet.addRow(wtRow);
  row.height = 18;
  
  applyStyle(row.getCell(1), COLORS.white);
  applyStyle(row.getCell(2), COLORS.white);
  
  colIdx = 3;
  dates.forEach((date, dateIdx) => {
    const isSunday = date.getDay() === 0;
    const isSaturday = date.getDay() === 6;
    const holidayInfo = isHoliday(date);
    let bgColor = COLORS.header;
    if (holidayInfo) bgColor = COLORS.holiday;
    else if (isSunday) bgColor = COLORS.sunday;
    else if (isSaturday) bgColor = COLORS.saturday;
    
    const isFirst = isFirstDayOfWeek(dateIdx);
    const isLast = isLastDayOfWeek(dateIdx);
    applyStyle(row.getCell(colIdx), bgColor, { bold: true, thickLeft: isFirst, thickBottom: true });
    applyStyle(row.getCell(colIdx + 1), bgColor, { bold: true, thickRight: isLast, thickBottom: true });
    colIdx += 2;
  });
  applyStyle(row.getCell(colIdx), COLORS.total, { thickLeft: true, thickBottom: true });
  applyStyle(row.getCell(colIdx + 1), COLORS.total, { thickBottom: true });

  // Style company info area A5:B7 with white background and no internal borders
  styleCompanyInfoArea(worksheet, 5, 7, 1, 2);
  
  if (companyInfo) {
    // Row 5: Logo embedded directly in the file
    await addLogoEmbedded(workbook, worksheet, companyInfo, 5, 1);
    worksheet.getRow(5).height = 35;
    
    // Row 6: Company name - merge A6:B6
    worksheet.mergeCells(6, 1, 6, 2);
    if (companyInfo.name) {
      const nameCell = worksheet.getCell(6, 1);
      nameCell.value = companyInfo.name;
      nameCell.font = { bold: true, size: 14, color: { argb: COLORS.grey } };
      nameCell.alignment = { horizontal: 'left', vertical: 'middle' };
    }
    
    // Row 7: Address - merge A7:B7
    worksheet.mergeCells(7, 1, 7, 2);
    if (companyInfo.address) {
      const addrCell = worksheet.getCell(7, 1);
      addrCell.value = companyInfo.address;
      addrCell.font = { size: 11, color: { argb: COLORS.grey } };
      addrCell.alignment = { horizontal: 'left', vertical: 'middle' };
    }
  }

  // Employee rows
  const dailyTotals = dates.map(() => ({ work: 0, travel: 0 }));
  let grandTotalWork = 0;
  let grandTotalTravel = 0;
  
  planEmployees.forEach(emp => {
    if (emp.isSpacer) {
      const spacerRow = [];
      const totalCols = 2 + (dates.length * 2) + 2;
      for (let i = 0; i < totalCols; i++) spacerRow.push('');
      row = worksheet.addRow(spacerRow);
      row.height = 20;
      applyStyle(row.getCell(1), COLORS.spacer);
      applyStyle(row.getCell(2), COLORS.spacer);
      let spacerColIdx = 3;
      dates.forEach((date, dateIdx) => {
        const isFirst = isFirstDayOfWeek(dateIdx);
        const isLast = isLastDayOfWeek(dateIdx);
        applyStyle(row.getCell(spacerColIdx), COLORS.spacer, { thickLeft: isFirst });
        applyStyle(row.getCell(spacerColIdx + 1), COLORS.spacer, { thickRight: isLast });
        spacerColIdx += 2;
      });
      applyStyle(row.getCell(spacerColIdx), COLORS.spacer, { thickLeft: true });
      applyStyle(row.getCell(spacerColIdx + 1), COLORS.spacer);
      return;
    }
    
    const empData = employees.find(e => e.id === emp.id);
    const empRow = [empData?.name || emp.name, emp.role || ''];
    let totalWork = 0;
    let totalTravel = 0;
    
    dates.forEach((date, dateIdx) => {
      const entry = getEntry(emp.id, date);
      const workHours = entry?.working_hours || 0;
      const travelHours = entry?.travel_hours || 0;
      
      const holidayInfo = isHoliday(date);
      let workValue = workHours || '';
      if (holidayInfo && !workHours) {
        if (holidayInfo.type === 'poland') workValue = '🇵🇱';
        else if (holidayInfo.type === 'project') workValue = '🏴';
        else workValue = '⭐';
      }
      
      empRow.push(workValue);
      empRow.push(travelHours || '');
      
      totalWork += workHours;
      totalTravel += travelHours;
      dailyTotals[dateIdx].work += workHours;
      dailyTotals[dateIdx].travel += travelHours;
    });
    
    empRow.push(totalWork);
    empRow.push(totalTravel);
    
    row = worksheet.addRow(empRow);
    row.height = 22;
    
    applyStyle(row.getCell(1), COLORS.employee, { align: 'left', bold: true });
    applyStyle(row.getCell(2), COLORS.white, { align: 'left' });
    
    colIdx = 3;
    dates.forEach((date, dateIdx) => {
      const entry = getEntry(emp.id, date);
      const holidayInfo = isHoliday(date);
      const isSunday = date.getDay() === 0;
      const isSaturday = date.getDay() === 6;
      const workHours = entry?.working_hours || 0;
      const travelHours = entry?.travel_hours || 0;
      
      let bgColor = COLORS.white;
      if (holidayInfo) bgColor = COLORS.holiday;
      else if (isSunday) bgColor = COLORS.sunday;
      else if (isSaturday) bgColor = COLORS.saturday;
      else if (workHours === 0 && travelHours === 0) bgColor = COLORS.nonWorking;
      
      const isFirst = isFirstDayOfWeek(dateIdx);
      const isLast = isLastDayOfWeek(dateIdx);
      applyStyle(row.getCell(colIdx), bgColor, { thickLeft: isFirst });
      applyStyle(row.getCell(colIdx + 1), bgColor, { thickRight: isLast });
      colIdx += 2;
    });
    
    applyStyle(row.getCell(colIdx), COLORS.total, { bold: true, thickLeft: true });
    applyStyle(row.getCell(colIdx + 1), COLORS.total, { bold: true });
    
    grandTotalWork += totalWork;
    grandTotalTravel += totalTravel;
  });

  // Totals row
  const totalsRow = [i18n.t('docx.totalCaps'), ''];
  dailyTotals.forEach(dt => {
    totalsRow.push(dt.work || '');
    totalsRow.push(dt.travel || '');
  });
  totalsRow.push(grandTotalWork);
  totalsRow.push(grandTotalTravel);
  
  row = worksheet.addRow(totalsRow);
  row.height = 25;
  
  applyStyle(row.getCell(1), COLORS.total, { bold: true, thickBorder: true });
  applyStyle(row.getCell(2), COLORS.total, { thickBorder: true });
  
  colIdx = 3;
  dates.forEach((_, dateIdx) => {
    const isFirst = isFirstDayOfWeek(dateIdx);
    const isLast = isLastDayOfWeek(dateIdx);
    applyStyle(row.getCell(colIdx), COLORS.total, { bold: true, thickBorder: true, thickLeft: isFirst });
    applyStyle(row.getCell(colIdx + 1), COLORS.total, { bold: true, thickBorder: true, thickRight: isLast });
    colIdx += 2;
  });
  applyStyle(row.getCell(colIdx), COLORS.total, { bold: true, thickBorder: true, thickLeft: true });
  applyStyle(row.getCell(colIdx + 1), COLORS.total, { bold: true, thickBorder: true });

  // Freeze panes
  worksheet.views = [
    { state: 'frozen', xSplit: 2, ySplit: 7, topLeftCell: 'C8', activeCell: 'C8' }
  ];

  // Add legend
  addLegend(worksheet, worksheet.rowCount + 2, [
    { color: COLORS.white, label: i18n.t('auto.lbl.workingDay') },
    { color: COLORS.nonWorking, label: i18n.t('auto.lbl.noHoursEntered') },
    { color: COLORS.saturday, label: i18n.t('auto.lbl.saturday') },
    { color: COLORS.sunday, label: i18n.t('auto.lbl.sunday') },
    { color: COLORS.holiday, label: i18n.t('auto.lbl.publicHoliday') },
    { color: COLORS.spacer, label: i18n.t('auto.lbl.spacerRow') },
    { color: COLORS.total, label: i18n.t('auto.lbl.totals') },
  ]);

  // Generate file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const planName = (plan.name || plan.project_name || 'Plan').replace(/[^a-z0-9]/gi, '_');
  const fileStartStr = format(planStart, 'yyyy-MM-dd');
  const fileEndStr = format(planEnd, 'yyyy-MM-dd');
  saveAs(blob, `${planName}_${fileStartStr}_to_${fileEndStr}.xlsx`);
  
  return true;
};

// Export Unified View to Excel with date range
// ─────────────────────────────────────────────────────────────────────
// exportUnifiedView (Excel)
// ─────────────────────────────────────────────────────────────────────
// Rewritten 2026-08-04 to derive availability from *project team-member
// assignments* (with the new whole_duration / spans window) rather than
// from Manpower Plans. Layout mirrors Team Availability so the two
// sheets read visually consistent: one row per active employee, one
// column per date, a checkmark when the employee is assigned that day.
// A single cell shows a checkmark regardless of how many hours would
// have been logged — the source of truth for hours is the timesheet.
export const exportUnifiedView = async ({
  projects,
  employees,
  startDate,
  endDate,
  companyInfo,
}) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(i18n.t('docx.unifiedOverview'));

  const dates = eachDayOfInterval({ start: new Date(startDate), end: new Date(endDate) });
  const activeEmployees = (employees || [])
    .filter(e => e.status === 'active' || e.is_active !== false)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  // Group dates by week for the two-row header (Week ##, Day-of-week).
  const weekGroups = [];
  let currentWeek = null;
  dates.forEach((date, idx) => {
    const weekNum = getWeekNumber(date);
    if (!currentWeek || currentWeek.weekNum !== weekNum) {
      currentWeek = { weekNum, days: [], startIdx: idx };
      weekGroups.push(currentWeek);
    }
    currentWeek.days.push(date);
  });

  // isMemberAssignedOnDate — inlined to keep this Excel export
  // self-contained. Mirrors /app/frontend/src/lib/projectAssignments.js.
  const _startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
  const _within = (d, from, to) => {
    if (from) { const f = _startOfDay(from); if (_startOfDay(d) < f) return false; }
    if (to)   { const t = _startOfDay(to);   if (_startOfDay(d) > t) return false; }
    return true;
  };
  const isAssigned = (project, member, date) => {
    if (member?.whole_duration !== false) {
      return _within(date, project?.start_date, project?.actual_end_date || project?.planned_end_date);
    }
    for (const sp of member?.spans || []) {
      if (!sp) continue;
      if (_within(date, sp.from_date, sp.to_date)) return true;
    }
    return false;
  };
  const projectsForEmployee = (empId, date) => {
    const out = [];
    for (const p of projects || []) {
      if (p.is_active === false) continue;
      const m = (p.team_members || []).find(tm => tm.employee_id === empId);
      if (!m) continue;
      if (isAssigned(p, m, date)) out.push(p);
    }
    return out;
  };

  // Column widths: name column + one narrow column per date.
  const colWidths = [{ width: 22 }];
  dates.forEach(() => colWidths.push({ width: 4 }));
  worksheet.columns = colWidths;

  // Row 1: Title
  let row = worksheet.addRow([i18n.t('docx.unifiedProjectOverview')]);
  row.height = 25;
  const titleCell = row.getCell(1);
  titleCell.font = { bold: true, size: 14, color: { argb: COLORS.grey } };
  titleCell.alignment = { horizontal: 'left', vertical: 'middle' };
  worksheet.mergeCells(1, 1, 1, Math.min(4, 1 + dates.length));

  // Row 2: Period
  row = worksheet.addRow([`Period: ${formatDateFull(startDate)} - ${formatDateFull(endDate)}`]);
  row.height = 18;
  row.getCell(1).font = { size: 10, color: { argb: COLORS.grey } };
  worksheet.mergeCells(2, 1, 2, Math.min(4, 1 + dates.length));

  // Add company logo/name block starting at row 3.
  await addLogoEmbedded(workbook, worksheet, companyInfo, 3, 1);

  // Reserve rows 3-5 for the logo/company text.
  const headerRow1Idx = 6; // Week numbers
  const headerRow2Idx = 7; // Day names + dates

  // Row 6: Week numbers
  const weekRowVals = [i18n.t('docx.employee')];
  dates.forEach(() => weekRowVals.push(''));
  const weekRow = worksheet.getRow(headerRow1Idx);
  weekRow.values = weekRowVals;
  weekGroups.forEach((week) => {
    const from = 2 + week.startIdx;
    const to = from + week.days.length - 1;
    if (to >= from) worksheet.mergeCells(headerRow1Idx, from, headerRow1Idx, to);
    const c = weekRow.getCell(from);
    c.value = `W${week.weekNum}`;
    c.font = { bold: true, size: 9, color: { argb: COLORS.grey } };
    c.alignment = { horizontal: 'center', vertical: 'middle' };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEDEDED' } };
    c.border = {
      top: { style: 'thin', color: { argb: 'FFBDBDBD' } },
      bottom: { style: 'thin', color: { argb: 'FFBDBDBD' } },
      left: { style: 'thin', color: { argb: 'FFBDBDBD' } },
      right: { style: 'thin', color: { argb: 'FFBDBDBD' } },
    };
  });
  const empHeaderCell = weekRow.getCell(1);
  empHeaderCell.value = i18n.t('docx.employee');
  empHeaderCell.font = { bold: true, size: 10, color: { argb: COLORS.headerText } };
  empHeaderCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.headerBg } };
  empHeaderCell.alignment = { horizontal: 'left', vertical: 'middle' };
  worksheet.mergeCells(headerRow1Idx, 1, headerRow2Idx, 1);

  // Row 7: Day names + dates (two lines)
  const dayRow = worksheet.getRow(headerRow2Idx);
  dates.forEach((date, i) => {
    const c = dayRow.getCell(2 + i);
    c.value = `${format(date, 'EEE', dfl())}\n${format(date, 'dd')}`;
    c.font = { bold: true, size: 8, color: { argb: COLORS.headerText } };
    c.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    const wd = date.getDay();
    if (wd === 0) c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.sunday } };
    else if (wd === 6) c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.saturday } };
    else c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.headerBg } };
    c.border = {
      top: { style: 'thin', color: { argb: 'FFBDBDBD' } },
      bottom: { style: 'thin', color: { argb: 'FFBDBDBD' } },
      left: { style: 'thin', color: { argb: 'FFBDBDBD' } },
      right: { style: 'thin', color: { argb: 'FFBDBDBD' } },
    };
  });
  dayRow.height = 26;

  // Body: one row per active employee.
  activeEmployees.forEach((emp) => {
    const bodyRow = worksheet.addRow([]);
    bodyRow.getCell(1).value = emp.name || '(unnamed)';
    bodyRow.getCell(1).font = { size: 10 };
    bodyRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
    dates.forEach((date, i) => {
      const c = bodyRow.getCell(2 + i);
      const assignedProjects = projectsForEmployee(emp.id, date);
      const wd = date.getDay();
      const isWeekend = wd === 0 || wd === 6;
      if (assignedProjects.length > 0) {
        // Store the numeric 1 (typed as number, not string) so
        // sum/count formulas across a column give a live headcount.
        c.value = 1;
        c.font = { size: 10, bold: true, color: { argb: 'FF166534' } }; // green
        c.alignment = { horizontal: 'center', vertical: 'middle' };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCFCE7' } }; // light green
        try {
          c.note = assignedProjects.map(p => p.name).join('\n');
        } catch { /* ExcelJS versions without note support */ }
      } else {
        c.value = '';
        c.alignment = { horizontal: 'center', vertical: 'middle' };
        if (wd === 0) c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.sunday } };
        else if (wd === 6) c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.saturday } };
      }
      c.border = {
        top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
        bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
        left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
        right: { style: 'thin', color: { argb: 'FFE0E0E0' } },
      };
      // Weekend-hover: keep the checkmark colored but signal the day.
      if (isWeekend && !assignedProjects.length) {
        c.font = { size: 9, color: { argb: 'FF9CA3AF' } };
      }
    });
    bodyRow.height = 18;
  });

  // Freeze the header rows + name column so the grid stays readable
  // when the sheet is wider than the viewport.
  worksheet.views = [{ state: 'frozen', xSplit: 1, ySplit: headerRow2Idx, topLeftCell: `B${headerRow2Idx + 1}` }];

  // Legend — write it manually so the long "1 = Assigned" label gets
  // its own row (the shared `addLegend` helper packs two entries per
  // row and clips long labels behind the next swatch).
  worksheet.addRow([]);
  const legendHeader = worksheet.addRow(['Legend:']);
  legendHeader.height = 20;
  legendHeader.getCell(1).font = { bold: true, size: 11, color: { argb: COLORS.grey } };
  const _addLegendRow = (swatchColor, label) => {
    const r = worksheet.addRow([]);
    r.height = 18;
    const sw = r.getCell(1);
    sw.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: swatchColor } };
    sw.border = {
      top: { style: 'thin', color: { argb: 'FFBDBDBD' } },
      bottom: { style: 'thin', color: { argb: 'FFBDBDBD' } },
      left: { style: 'thin', color: { argb: 'FFBDBDBD' } },
      right: { style: 'thin', color: { argb: 'FFBDBDBD' } },
    };
    const lbl = r.getCell(2);
    lbl.value = label;
    lbl.font = { size: 9, color: { argb: COLORS.grey } };
    lbl.alignment = { horizontal: 'left', vertical: 'middle' };
    // Merge across enough columns for the sentence to breathe.
    worksheet.mergeCells(r.number, 2, r.number, Math.min(2 + 8, 1 + dates.length));
  };
  _addLegendRow('FFDCFCE7', '1 = Assigned to a project team (sum a column to get daily headcount)');
  _addLegendRow(COLORS.saturday, i18n.t('docx.saturday'));
  _addLegendRow(COLORS.sunday, i18n.t('docx.sunday'));
  // Human-readable footer note.
  const noteRow = worksheet.addRow(['Hover a "1" cell to see the project name(s). Hours worked are recorded on timesheets.']);
  noteRow.getCell(1).font = { italic: true, size: 9, color: { argb: COLORS.grey } };
  worksheet.mergeCells(noteRow.number, 1, noteRow.number, Math.min(8, 1 + dates.length));

  // Generate file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, `Unified_Overview_${format(new Date(startDate), 'yyyy-MM-dd')}_to_${format(new Date(endDate), 'yyyy-MM-dd')}.xlsx`);

  return true;
};


// ========== PDF EXPORT ==========
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─── Unicode font loader ────────────────────────────────────────────
// jsPDF's built-in helvetica is limited to WinAnsi and mangles Polish
// diacritics (ł/ą/ę/ś/ć/ź/ż/ń/ó → boxes or wrong glyphs). We fetch a
// Unicode-capable font once per session from a CDN, cache it as base64
// in-memory, and register it inside every jsPDF document that opts in
// via ``registerUnicodeFont(doc)``. If the CDN is unreachable we fall
// through to helvetica and transliterate Polish diacritics to ASCII so
// text is at least legible — worst case: "Łukasz" reads "Lukasz".
const _FONT_URL = 'https://cdn.jsdelivr.net/npm/@fontsource/noto-sans@5.0.11/files/noto-sans-latin-ext-400-normal.woff';
const _FONT_TTF_URL = 'https://cdn.jsdelivr.net/npm/@fontsource/roboto@5.0.13/files/roboto-latin-ext-400-normal.woff';
// Prefer the more widely-cached TTF from Google's own Font API mirror.
const _FONT_TTF_PRIMARY = 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts/hinted/ttf/NotoSans/NotoSans-Regular.ttf';
let _fontBase64Cache = null;
let _fontFailed = false;

const _fetchAsBase64 = async (url) => {
  const res = await fetch(url, { mode: 'cors' });
  if (!res.ok) throw new Error(`Font HTTP ${res.status}`);
  const buf = await res.arrayBuffer();
  // Convert ArrayBuffer -> base64 in chunks to avoid stack overflows.
  const bytes = new Uint8Array(buf);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
};

const _loadUnicodeFontBase64 = async () => {
  if (_fontBase64Cache) return _fontBase64Cache;
  if (_fontFailed) return null;
  try {
    _fontBase64Cache = await _fetchAsBase64(_FONT_TTF_PRIMARY);
    return _fontBase64Cache;
  } catch (_e1) {
    try {
      // Fallback: try a Noto-Sans TTF hosted on unpkg.
      _fontBase64Cache = await _fetchAsBase64('https://unpkg.com/@fontsource/noto-sans@5.0.11/files/noto-sans-latin-ext-400-normal.woff');
      return _fontBase64Cache;
    } catch (_e2) {
      _fontFailed = true;
      return null;
    }
  }
};

// Best-effort Unicode registration for `doc`. Returns the font-name to
// use (`'NotoSans'` when the fetch succeeded, otherwise `'helvetica'`).
const registerUnicodeFont = async (doc) => {
  try {
    const b64 = await _loadUnicodeFontBase64();
    if (!b64) return 'helvetica';
    doc.addFileToVFS('NotoSans-Regular.ttf', b64);
    doc.addFont('NotoSans-Regular.ttf', 'NotoSans', 'normal');
    doc.addFont('NotoSans-Regular.ttf', 'NotoSans', 'bold');
    return 'NotoSans';
  } catch { return 'helvetica'; }
};

// ASCII transliteration for the helvetica fallback. Keeps names
// readable even without a Unicode font.
const _PL_MAP = {
  'ą':'a','ć':'c','ę':'e','ł':'l','ń':'n','ó':'o','ś':'s','ź':'z','ż':'z',
  'Ą':'A','Ć':'C','Ę':'E','Ł':'L','Ń':'N','Ó':'O','Ś':'S','Ź':'Z','Ż':'Z',
};
const _asciiFallback = (s) => (s || '').replace(/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g, (c) => _PL_MAP[c] || c);

// PDF color palette (RGB arrays for jsPDF compatibility)
const PDF_RGB = {
  grey: [77, 77, 77],
  darkGrey: [50, 50, 50],
  header: [232, 232, 232],
  weekHeaderOdd: [217, 217, 217],
  weekHeaderEven: [196, 196, 196],
  dayHeader: [227, 242, 253],
  sunday: [255, 235, 235],
  saturday: [255, 248, 225],
  holiday: [255, 236, 179],
  total: [232, 234, 246],
  white: [255, 255, 255],
  employee: [249, 250, 251],
  lightGrey: [150, 150, 150],
  available: [232, 245, 233],
  unavailable: [255, 235, 238],
  workCol: [248, 250, 252],
  travelCol: [241, 245, 249],
};

// Helper to fetch and embed logo in PDF with proper aspect ratio
const addLogoPDF = async (doc, companyInfo, margin) => {
  if (!companyInfo) return margin;
  
  const logoUrl = companyInfo.header_logo_file || companyInfo.logo_file;
  if (!logoUrl) return margin;
  
  try {
    const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
    const fullLogoUrl = logoUrl.startsWith('http') ? logoUrl : 
      logoUrl.startsWith('/api/') ? `${API_BASE}${logoUrl}` : 
      `${API_BASE}/api/files/logo/${logoUrl}`;
    
    const logoResponse = await fetch(fullLogoUrl);
    if (!logoResponse.ok) return margin;
    
    const logoBlob = await logoResponse.blob();
    const logoBase64 = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(logoBlob);
    });
    
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = logoBase64;
    });
    
    const maxLogoHeight = 10;
    const maxLogoWidth = 30;
    const widthRatio = maxLogoWidth / img.width;
    const heightRatio = maxLogoHeight / img.height;
    const scale = Math.min(widthRatio, heightRatio, 1);
    
    const logoWidth = img.width * scale;
    const logoHeight = img.height * scale;
    
    doc.addImage(logoBase64, 'PNG', margin, margin, logoWidth, logoHeight);
    return margin + logoWidth + 3;
  } catch (error) {
    console.warn('Could not embed logo in PDF:', error);
    return margin;
  }
};

// Export Project Plan to PDF - Matching Excel format with W/T columns per day
export const exportProjectPlanPDF = async ({
  plan,
  planEntries,
  planEmployees,
  polandHolidays,
  projectHolidays,
  customNonWorkingDays,
  startDate,
  endDate,
  employees,
  companyInfo
}) => {
  try {
    const planStart = startDate ? new Date(startDate) : new Date(plan.start_date);
    const planEnd = endDate ? new Date(endDate) : new Date(plan.end_date);
    const dates = eachDayOfInterval({ start: planStart, end: planEnd });
    
    // Group dates by week
    const weekGroups = [];
    let currentWeek = null;
    dates.forEach((date, idx) => {
      const weekNum = getWeekNumber(date);
      if (!currentWeek || currentWeek.weekNum !== weekNum) {
        currentWeek = { weekNum, days: [], startIdx: idx };
        weekGroups.push(currentWeek);
      }
      currentWeek.days.push(date);
    });

    // Get entry for employee on date
    const getEntry = (employeeId, date) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      return planEntries.find(e => 
        e.employee_id === employeeId && 
        (e.date.split('T')[0] === dateStr || format(new Date(e.date), 'yyyy-MM-dd') === dateStr)
      );
    };

    // Check if date is holiday
    const isHoliday = (date) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const allHolidays = [...(polandHolidays || []), ...(projectHolidays || []), ...(customNonWorkingDays || [])];
      return allHolidays.find(h => h.date === dateStr);
    };

    // Create PDF
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 5;

    // Add header
    let logoEndX = await addLogoPDF(doc, companyInfo, margin);
    if (companyInfo) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...PDF_RGB.grey);
      if (companyInfo.name) doc.text(companyInfo.name, logoEndX, margin + 4);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      if (companyInfo.address) doc.text(companyInfo.address, logoEndX, margin + 8);
    }

    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`Project Plan: ${plan.name || plan.project_name}`, margin, margin + 16);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text(`Period: ${format(planStart, 'yyyy.MM.dd')} - ${format(planEnd, 'yyyy.MM.dd')}`, margin, margin + 20);

    // Build table headers - Row 1: Week numbers, Row 2: Day names, Row 3: Dates, Row 4: W/T
    const colCount = 2 + dates.length * 2 + 2; // Employee, Role, (W,T)*days, TotalW, TotalT
    
    // Calculate column widths
    const empColWidth = 22;
    const roleColWidth = 12;
    const totalColWidth = 8;
    const availableWidth = pageWidth - margin * 2 - empColWidth - roleColWidth - totalColWidth * 2;
    const dayColWidth = Math.max(availableWidth / (dates.length * 2), 4);

    // Build the 4 header rows
    const head = [];
    
    // Row 1: Week numbers (merged spans)
    const weekRow = [{ content: i18n.t('docx.employee'), rowSpan: 4, styles: { halign: 'left', valign: 'middle' } }, 
                     { content: i18n.t('docx.role'), rowSpan: 4, styles: { halign: 'left', valign: 'middle' } }];
    weekGroups.forEach((week, wIdx) => {
      weekRow.push({ 
        content: `W${week.weekNum}`, 
        colSpan: week.days.length * 2,
        styles: { fillColor: wIdx % 2 === 0 ? PDF_RGB.weekHeaderOdd : PDF_RGB.weekHeaderEven, halign: 'center' }
      });
    });
    weekRow.push({ content: i18n.t('docx.total'), colSpan: 2, rowSpan: 3, styles: { halign: 'center', valign: 'middle' } });
    head.push(weekRow);

    // Row 2: Day names (Mon, Tue, etc.) - each spans 2 cols (W, T)
    const dayNameRow = [];
    dates.forEach(d => {
      dayNameRow.push({ content: format(d, 'EEE', dfl()), colSpan: 2, styles: { halign: 'center', fontSize: 5 } });
    });
    head.push(dayNameRow);

    // Row 3: Dates (01, 02, etc.) - each spans 2 cols
    const dateRow = [];
    dates.forEach(d => {
      dateRow.push({ content: format(d, 'dd'), colSpan: 2, styles: { halign: 'center', fontSize: 5 } });
    });
    head.push(dateRow);

    // Row 4: W/T alternating for each day
    const wtRow = [];
    dates.forEach(() => {
      wtRow.push({ content: 'W', styles: { halign: 'center', fontSize: 5, fillColor: PDF_RGB.workCol } });
      wtRow.push({ content: 'T', styles: { halign: 'center', fontSize: 5, fillColor: PDF_RGB.travelCol } });
    });
    wtRow.push({ content: 'W', styles: { halign: 'center', fontSize: 5, fontStyle: 'bold' } });
    wtRow.push({ content: 'T', styles: { halign: 'center', fontSize: 5, fontStyle: 'bold' } });
    head.push(wtRow);

    // Build body rows
    const body = [];
    const dayTotals = dates.map(() => ({ work: 0, travel: 0 }));
    let grandTotalWork = 0;
    let grandTotalTravel = 0;

    planEmployees.forEach(emp => {
      if (emp.isSpacer) return;
      
      const empData = employees.find(e => e.id === emp.id) || employees.find(e => e.id === emp.employee_id);
      const empName = empData?.name || emp.name || i18n.t('docx.unknown');
      const empRole = emp.role || '';
      const empId = emp.id || emp.employee_id;
      
      const row = [empName, empRole];
      let empTotalWork = 0;
      let empTotalTravel = 0;
      
      dates.forEach((date, idx) => {
        const entry = getEntry(empId, date);
        const work = entry?.working_hours || 0;
        const travel = entry?.travel_hours || 0;
        
        row.push(work || '');
        row.push(travel || '');
        
        dayTotals[idx].work += work;
        dayTotals[idx].travel += travel;
        empTotalWork += work;
        empTotalTravel += travel;
      });
      
      row.push(empTotalWork || '');
      row.push(empTotalTravel || '');
      grandTotalWork += empTotalWork;
      grandTotalTravel += empTotalTravel;
      
      body.push(row);
    });

    // Add totals row
    const totalsRow = [i18n.t('docx.totalCaps'), ''];
    dates.forEach((_, idx) => {
      totalsRow.push(dayTotals[idx].work || '');
      totalsRow.push(dayTotals[idx].travel || '');
    });
    totalsRow.push(grandTotalWork);
    totalsRow.push(grandTotalTravel);
    body.push(totalsRow);

    // Column styles
    const columnStyles = {
      0: { cellWidth: empColWidth, halign: 'left', fontSize: 6 },
      1: { cellWidth: roleColWidth, halign: 'left', fontSize: 6 },
    };
    for (let i = 2; i < 2 + dates.length * 2; i++) {
      columnStyles[i] = { cellWidth: dayColWidth, halign: 'center', fontSize: 5 };
    }
    columnStyles[2 + dates.length * 2] = { cellWidth: totalColWidth, halign: 'center', fontSize: 6, fontStyle: 'bold' };
    columnStyles[3 + dates.length * 2] = { cellWidth: totalColWidth, halign: 'center', fontSize: 6, fontStyle: 'bold' };

    // Generate table
    autoTable(doc, {
      head: head,
      body: body,
      startY: margin + 23,
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 5,
        cellPadding: 0.8,
        lineColor: [180, 180, 180],
        lineWidth: 0.1,
        font: 'helvetica',
      },
      headStyles: {
        fillColor: PDF_RGB.dayHeader,
        textColor: PDF_RGB.darkGrey,
        fontStyle: 'bold',
        halign: 'center',
        fontSize: 5,
        cellPadding: 1,
      },
      columnStyles: columnStyles,
      alternateRowStyles: { fillColor: PDF_RGB.employee },
      didParseCell: function(data) {
        if (data.section === 'body') {
          const isLastRow = data.row.index === body.length - 1;
          if (isLastRow) {
            data.cell.styles.fillColor = PDF_RGB.total;
            data.cell.styles.fontStyle = 'bold';
          }
          // Color W/T columns
          const colIdx = data.column.index;
          if (colIdx >= 2 && colIdx < 2 + dates.length * 2) {
            const dateIdx = Math.floor((colIdx - 2) / 2);
            const isWorkCol = (colIdx - 2) % 2 === 0;
            const date = dates[dateIdx];
            const isSunday = date.getDay() === 0;
            const isSaturday = date.getDay() === 6;
            const holiday = isHoliday(date);
            
            if (!isLastRow) {
              if (holiday) data.cell.styles.fillColor = PDF_RGB.holiday;
              else if (isSunday) data.cell.styles.fillColor = PDF_RGB.sunday;
              else if (isSaturday) data.cell.styles.fillColor = PDF_RGB.saturday;
            }
          }
        }
      },
      showHead: 'everyPage',
    });

    // Legend
    const finalY = doc.lastAutoTable.finalY + 3;
    doc.setFontSize(5);
    doc.setTextColor(100, 100, 100);
    doc.text('W = Working Hours, T = Travel Hours', margin, finalY);

    // Page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(6);
      doc.setTextColor(...PDF_RGB.lightGrey);
      doc.text(`Page ${i}/${pageCount}`, pageWidth - margin - 12, pageHeight - 3);
      doc.text(`Generated: ${format(new Date(), 'yyyy.MM.dd HH:mm')}`, margin, pageHeight - 3);
    }

    const planName = (plan.name || plan.project_name || 'Plan').replace(/[^a-z0-9]/gi, '_');
    doc.save(`${planName}_${format(planStart, 'yyyy-MM-dd')}_to_${format(planEnd, 'yyyy-MM-dd')}.pdf`);
    
    return true;
  } catch (error) {
    console.error('Error generating Project Plan PDF:', error);
    throw error;
  }
};

// Export Team Availability to PDF - Matching Excel format
export const exportTeamAvailabilityPDF = async ({
  employees,
  availability,
  holidays,
  startDate,
  endDate,
  getAvailabilityTypeInfo,
  companyInfo
}) => {
  try {
    const dates = eachDayOfInterval({ start: new Date(startDate), end: new Date(endDate) });
    const activeEmployees = employees.filter(e => e.status === 'active' || e.is_active !== false);

    // Group dates by week
    const weekGroups = [];
    let currentWeek = null;
    dates.forEach((date, idx) => {
      const weekNum = getWeekNumber(date);
      if (!currentWeek || currentWeek.weekNum !== weekNum) {
        currentWeek = { weekNum, days: [], startIdx: idx };
        weekGroups.push(currentWeek);
      }
      currentWeek.days.push(date);
    });

    // Check if date is a holiday
    const isHoliday = (date) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      return holidays.find(h => h.date === dateStr);
    };

    // Get employee availability for a date
    const getEmployeeAvailability = (employeeId, date) => {
      return availability.filter(a => {
        const start = new Date(a.start_date);
        const end = new Date(a.end_date);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        const checkDate = new Date(date);
        checkDate.setHours(12, 0, 0, 0);
        return a.employee_id === employeeId && checkDate >= start && checkDate <= end;
      });
    };

    // Create PDF
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 5;

    // Register a Unicode-capable font so Polish diacritics render
    // correctly. If the fetch fails we fall back to helvetica +
    // ASCII transliteration (see `_asciiFallback` below).
    const activeFont = await registerUnicodeFont(doc);
    const fixText = (s) => (activeFont === 'NotoSans' ? String(s ?? '') : _asciiFallback(String(s ?? '')));

    // Add header
    let logoEndX = await addLogoPDF(doc, companyInfo, margin);
    if (companyInfo) {
      doc.setFont(activeFont, 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...PDF_RGB.grey);
      if (companyInfo.name) doc.text(fixText(companyInfo.name), logoEndX, margin + 4);
      doc.setFont(activeFont, 'normal');
      doc.setFontSize(7);
      if (companyInfo.address) doc.text(fixText(companyInfo.address), logoEndX, margin + 8);
    }

    // Title
    doc.setFont(activeFont, 'bold');
    doc.setFontSize(10);
    doc.text(i18n.t('docx.teamAvailabilityOverview'), margin, margin + 16);
    doc.setFont(activeFont, 'normal');
    doc.setFontSize(7);
    doc.text(`Period: ${formatDateFull(new Date(startDate))} - ${formatDateFull(new Date(endDate))}`, margin, margin + 20);

    // ─── Column-wrap across pages ─────────────────────────────────
    // A4 landscape is 297mm wide; with a 22mm employee column and a
    // reasonable minimum 6mm per date column, we can fit ~45 dates
    // per page. Round the chunk boundary to week-starts so each page
    // remains readable at a glance and doesn't split a Monday from
    // its Sunday.
    const empColWidth = 22;
    const minDayColWidth = 6;
    const availableWidth = pageWidth - margin * 2 - empColWidth;
    const maxDaysPerPage = Math.max(7, Math.floor(availableWidth / minDayColWidth));
    // Uniform day-column width across ALL pages — sized to the widest
    // chunk. The final short page leaves whitespace on the right
    // rather than stretching to fill the page. Matches user request.
    const masterDayColWidth = Math.max(availableWidth / maxDaysPerPage, minDayColWidth);

    // Chunk weekGroups so each chunk sums to ≤ maxDaysPerPage days.
    const chunks = [];
    let curDays = [];
    let curWeeks = [];
    for (const wk of weekGroups) {
      if (curDays.length + wk.days.length > maxDaysPerPage && curDays.length > 0) {
        chunks.push({ dates: curDays, weeks: curWeeks });
        curDays = [];
        curWeeks = [];
      }
      const wkStart = curDays.length;
      curDays.push(...wk.days);
      curWeeks.push({ weekNum: wk.weekNum, days: wk.days, startIdx: wkStart });
    }
    if (curDays.length) chunks.push({ dates: curDays, weeks: curWeeks });

    for (let chunkIdx = 0; chunkIdx < chunks.length; chunkIdx++) {
      const { dates: chunkDates, weeks: chunkWeeks } = chunks[chunkIdx];
      if (chunkIdx > 0) {
        doc.addPage('a4', 'landscape');
        // Repeat title/period on every page so a printout still makes
        // sense if pages get separated.
        doc.setFont(activeFont, 'bold');
        doc.setFontSize(9);
        doc.setTextColor(...PDF_RGB.grey);
        doc.text(i18n.t('docx.teamAvailabilityOverview'), margin, margin + 4);
        doc.setFont(activeFont, 'normal');
        doc.setFontSize(7);
        doc.text(`Period: ${formatDateFull(new Date(startDate))} - ${formatDateFull(new Date(endDate))} — page ${chunkIdx + 1}/${chunks.length}`, margin, margin + 8);
      }
      const startY = chunkIdx === 0 ? margin + 23 : margin + 12;
      const dayColWidth = masterDayColWidth;

      // Chunk-local first/last day helpers.
      const chunkIsFirstDayOfWeek = (dateIdx) => chunkWeeks.some(w => dateIdx === w.startIdx);
      const chunkIsLastDayOfWeek = (dateIdx) => chunkWeeks.some(w => dateIdx === w.startIdx + w.days.length - 1);

      // Build headers - 4 rows: Month, Week, Day name, Date
      const head = [];

      // Row 0: Month names — one merged cell per contiguous run of
      // days sharing the same month. Users flip pages quickly and
      // "W35" alone doesn't tell them if they're in September or
      // October.
      const monthRow = [{ content: i18n.t('docx.employee'), rowSpan: 4, styles: { halign: 'left', valign: 'middle' } }];
      const monthGroups = [];
      chunkDates.forEach((d, idx) => {
        const key = format(d, 'yyyy-MM');
        const label = format(d, 'LLLL yyyy', dfl());
        const last = monthGroups[monthGroups.length - 1];
        if (last && last.key === key) last.count += 1;
        else monthGroups.push({ key, label, count: 1, startIdx: idx });
      });
      monthGroups.forEach((mg, mIdx) => {
        monthRow.push({
          content: mg.label,
          colSpan: mg.count,
          styles: { fillColor: mIdx % 2 === 0 ? PDF_RGB.weekHeaderEven : PDF_RGB.weekHeaderOdd, halign: 'center', fontStyle: 'bold', fontSize: 6 }
        });
      });
      head.push(monthRow);

      // Row 1: Week numbers
      const weekRow = [];
      chunkWeeks.forEach((week, wIdx) => {
        weekRow.push({
          content: `W${week.weekNum}`,
          colSpan: week.days.length,
          styles: { fillColor: wIdx % 2 === 0 ? PDF_RGB.weekHeaderOdd : PDF_RGB.weekHeaderEven, halign: 'center' }
        });
      });
      head.push(weekRow);

      // Row 2: Day names
      const dayNameRow = [];
      chunkDates.forEach(d => {
        const isSunday = d.getDay() === 0;
        const isSaturday = d.getDay() === 6;
        dayNameRow.push({
          content: format(d, 'EEE', dfl()),
          styles: {
            halign: 'center',
            fontSize: 5,
            fillColor: isSunday ? PDF_RGB.sunday : (isSaturday ? PDF_RGB.saturday : PDF_RGB.dayHeader)
          }
        });
      });
      head.push(dayNameRow);

      // Row 3: Dates
      const dateRow = [];
      chunkDates.forEach(d => {
        const isSunday = d.getDay() === 0;
        const isSaturday = d.getDay() === 6;
        dateRow.push({
          content: format(d, 'dd'),
          styles: {
            halign: 'center',
            fontSize: 5,
            fillColor: isSunday ? PDF_RGB.sunday : (isSaturday ? PDF_RGB.saturday : PDF_RGB.dayHeader)
          }
        });
      });
      head.push(dateRow);

      // Build body rows for this chunk
      const body = [];
      activeEmployees.forEach(emp => {
        const row = [fixText(emp.name)];
        chunkDates.forEach(date => {
          const isSunday = date.getDay() === 0;
          const isSaturday = date.getDay() === 6;
          const holiday = isHoliday(date);
          if (holiday) {
            row.push('H');
          } else {
            const empAvailability = getEmployeeAvailability(emp.id, date);
            if (empAvailability.length > 0) {
              const types = empAvailability.map(a => {
                const typeInfo = getAvailabilityTypeInfo(a.availability_type);
                return typeInfo?.shortLabel?.substring(0, 2) || a.availability_type?.substring(0, 2)?.toUpperCase() || '?';
              });
              row.push(types.join('/'));
            } else if (isSunday || isSaturday) {
              row.push('');
            } else {
              row.push('\u2713');
            }
          }
        });
        body.push(row);
      });

      // Column styles for this chunk
      const columnStyles = { 0: { cellWidth: empColWidth, halign: 'left', fontSize: 6 } };
      for (let i = 1; i <= chunkDates.length; i++) {
        columnStyles[i] = { cellWidth: dayColWidth, halign: 'center', fontSize: 5 };
      }

      autoTable(doc, {
        head, body, startY,
        margin: { left: margin, right: margin },
        styles: {
          fontSize: 5,
          cellPadding: 0.8,
          lineColor: [180, 180, 180],
          lineWidth: 0.1,
          font: activeFont,
        },
        headStyles: {
          fillColor: PDF_RGB.dayHeader,
          textColor: PDF_RGB.darkGrey,
          fontStyle: 'bold',
          halign: 'center',
          fontSize: 5,
          cellPadding: 1,
        },
        columnStyles,
        alternateRowStyles: { fillColor: PDF_RGB.employee },
        didParseCell: function(data) {
          if (data.section === 'body') {
            const colIdx = data.column.index;
            if (colIdx > 0) {
              const dateIdx = colIdx - 1;
              if (dateIdx >= 0 && dateIdx < chunkDates.length) {
                const date = chunkDates[dateIdx];
                const isSunday = date.getDay() === 0;
                const isSaturday = date.getDay() === 6;
                const holiday = isHoliday(date);
                const cellContent = data.cell.raw;
                if (holiday) data.cell.styles.fillColor = PDF_RGB.holiday;
                else if (isSunday) data.cell.styles.fillColor = PDF_RGB.sunday;
                else if (isSaturday) data.cell.styles.fillColor = PDF_RGB.saturday;
                else if (cellContent && cellContent !== '\u2713') data.cell.styles.fillColor = PDF_RGB.unavailable;
                else if (cellContent === '\u2713') data.cell.styles.fillColor = PDF_RGB.available;
              }
            }
          }
        },
        showHead: 'everyPage',
      });
    }

    // Legend on the last page
    const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 3 : pageHeight - 8;
    doc.setFontSize(5);
    doc.setTextColor(100, 100, 100);
    doc.text('\u2713 = Available, H = Holiday, VA = Vacation, SI = Sick, PE = Personal', margin, Math.min(finalY, pageHeight - 5));

    // Page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(6);
      doc.setTextColor(...PDF_RGB.lightGrey);
      doc.text(`Page ${i}/${pageCount}`, pageWidth - margin - 12, pageHeight - 3);
      doc.text(`Generated: ${format(new Date(), 'yyyy.MM.dd HH:mm')}`, margin, pageHeight - 3);
    }

    const fileStartStr = format(new Date(startDate), 'yyyy-MM-dd');
    const fileEndStr = format(new Date(endDate), 'yyyy-MM-dd');
    doc.save(`Team_Availability_${fileStartStr}_to_${fileEndStr}.pdf`);
    
    return true;
  } catch (error) {
    console.error('Error generating Team Availability PDF:', error);
    throw error;
  }
};

// Export Unified Overview to PDF - Matching Excel format
// ─────────────────────────────────────────────────────────────────────
// exportUnifiedOverviewPDF
// ─────────────────────────────────────────────────────────────────────
// Rewritten 2026-08-04 to mirror the Excel export: rows = active
// employees, columns = dates, cell = ✓ when the employee is on a
// project team on that day. Multi-page column wrapping + NotoSans
// Unicode font (fetched from CDN, cached in-memory, with an
// ASCII-transliteration fallback) so Polish diacritics render
// correctly. Legend appears on *every* page.
export const exportUnifiedOverviewPDF = async ({
  projects,
  employees,
  startDate,
  endDate,
  companyInfo,
}) => {
  try {
    const dates = eachDayOfInterval({ start: new Date(startDate), end: new Date(endDate) });
    const activeEmployees = (employees || [])
      .filter(e => e.status === 'active' || e.is_active !== false)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    // Group dates by week
    const weekGroups = [];
    let currentWeek = null;
    dates.forEach((date, idx) => {
      const weekNum = getWeekNumber(date);
      if (!currentWeek || currentWeek.weekNum !== weekNum) {
        currentWeek = { weekNum, days: [], startIdx: idx };
        weekGroups.push(currentWeek);
      }
      currentWeek.days.push(date);
    });

    // isAssigned — mirrors /app/frontend/src/lib/projectAssignments.js
    const _startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
    const _within = (d, from, to) => {
      if (from) { const f = _startOfDay(from); if (_startOfDay(d) < f) return false; }
      if (to)   { const t = _startOfDay(to);   if (_startOfDay(d) > t) return false; }
      return true;
    };
    const isAssigned = (project, member, date) => {
      if (member?.whole_duration !== false) {
        return _within(date, project?.start_date, project?.actual_end_date || project?.planned_end_date);
      }
      for (const sp of member?.spans || []) {
        if (!sp) continue;
        if (_within(date, sp.from_date, sp.to_date)) return true;
      }
      return false;
    };
    const projectsForEmployee = (empId, date) => {
      const out = [];
      for (const p of projects || []) {
        if (p.is_active === false) continue;
        const m = (p.team_members || []).find(tm => tm.employee_id === empId);
        if (!m) continue;
        if (isAssigned(p, m, date)) out.push(p);
      }
      return out;
    };

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 5;
    const activeFont = await registerUnicodeFont(doc);
    const fixText = (s) => (activeFont === 'NotoSans' ? String(s ?? '') : _asciiFallback(String(s ?? '')));

    // Add header (logo + company + title + period)
    let logoEndX = await addLogoPDF(doc, companyInfo, margin);
    if (companyInfo) {
      doc.setFont(activeFont, 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...PDF_RGB.grey);
      if (companyInfo.name) doc.text(fixText(companyInfo.name), logoEndX, margin + 4);
      doc.setFont(activeFont, 'normal');
      doc.setFontSize(7);
      if (companyInfo.address) doc.text(fixText(companyInfo.address), logoEndX, margin + 8);
    }
    doc.setFont(activeFont, 'bold');
    doc.setFontSize(10);
    doc.text(i18n.t('docx.unifiedProjectOverview'), margin, margin + 16);
    doc.setFont(activeFont, 'normal');
    doc.setFontSize(7);
    doc.text(`Period: ${formatDateFull(new Date(startDate))} - ${formatDateFull(new Date(endDate))}`, margin, margin + 20);

    // Column-wrap across pages
    const empColWidth = 30;
    const minDayColWidth = 6;
    const availableWidth = pageWidth - margin * 2 - empColWidth;
    const maxDaysPerPage = Math.max(7, Math.floor(availableWidth / minDayColWidth));
    // Uniform day-column width across ALL pages — sized to the widest
    // chunk. Short final page leaves whitespace on the right rather
    // than stretching (matches user request).
    const masterDayColWidth = Math.max(availableWidth / maxDaysPerPage, minDayColWidth);

    const chunks = [];
    let curDays = [];
    let curWeeks = [];
    for (const wk of weekGroups) {
      if (curDays.length + wk.days.length > maxDaysPerPage && curDays.length > 0) {
        chunks.push({ dates: curDays, weeks: curWeeks });
        curDays = []; curWeeks = [];
      }
      const wkStart = curDays.length;
      curDays.push(...wk.days);
      curWeeks.push({ weekNum: wk.weekNum, days: wk.days, startIdx: wkStart });
    }
    if (curDays.length) chunks.push({ dates: curDays, weeks: curWeeks });

    const drawLegend = (yPos) => {
      doc.setFontSize(5);
      doc.setTextColor(100, 100, 100);
      doc.setFont(activeFont, 'normal');
      doc.text(fixText('1 = Assigned to a project team (count is sum-able). Empty cell = not assigned. Hours worked are recorded on timesheets.'), margin, yPos);
    };

    for (let chunkIdx = 0; chunkIdx < chunks.length; chunkIdx++) {
      const { dates: chunkDates, weeks: chunkWeeks } = chunks[chunkIdx];
      if (chunkIdx > 0) {
        doc.addPage('a4', 'landscape');
        doc.setFont(activeFont, 'bold');
        doc.setFontSize(9);
        doc.setTextColor(...PDF_RGB.grey);
        doc.text(i18n.t('docx.unifiedProjectOverview'), margin, margin + 4);
        doc.setFont(activeFont, 'normal');
        doc.setFontSize(7);
        doc.text(`Period: ${formatDateFull(new Date(startDate))} - ${formatDateFull(new Date(endDate))} — page ${chunkIdx + 1}/${chunks.length}`, margin, margin + 8);
      }
      const startY = chunkIdx === 0 ? margin + 23 : margin + 12;
      const dayColWidth = masterDayColWidth;

      // Build 4-row header: Month, Week, Day, Date
      const head = [];
      // Row 0: Months
      const monthRow = [{ content: i18n.t('docx.employee'), rowSpan: 4, styles: { halign: 'left', valign: 'middle' } }];
      const monthGroups = [];
      chunkDates.forEach((d, idx) => {
        const key = format(d, 'yyyy-MM');
        const label = format(d, 'LLLL yyyy', dfl());
        const last = monthGroups[monthGroups.length - 1];
        if (last && last.key === key) last.count += 1;
        else monthGroups.push({ key, label, count: 1, startIdx: idx });
      });
      monthGroups.forEach((mg, mIdx) => {
        monthRow.push({
          content: mg.label,
          colSpan: mg.count,
          styles: { fillColor: mIdx % 2 === 0 ? PDF_RGB.weekHeaderEven : PDF_RGB.weekHeaderOdd, halign: 'center', fontStyle: 'bold', fontSize: 6 },
        });
      });
      head.push(monthRow);
      const weekRow = [];
      chunkWeeks.forEach((week, wIdx) => {
        weekRow.push({
          content: `W${week.weekNum}`,
          colSpan: week.days.length,
          styles: { fillColor: wIdx % 2 === 0 ? PDF_RGB.weekHeaderOdd : PDF_RGB.weekHeaderEven, halign: 'center' },
        });
      });
      head.push(weekRow);
      const dayNameRow = [];
      const dateRow = [];
      chunkDates.forEach((d) => {
        const isSunday = d.getDay() === 0;
        const isSaturday = d.getDay() === 6;
        const fillColor = isSunday ? PDF_RGB.sunday : (isSaturday ? PDF_RGB.saturday : PDF_RGB.dayHeader);
        dayNameRow.push({ content: format(d, 'EEE', dfl()), styles: { halign: 'center', fontSize: 5, fillColor } });
        dateRow.push({ content: format(d, 'dd'), styles: { halign: 'center', fontSize: 5, fillColor } });
      });
      head.push(dayNameRow);
      head.push(dateRow);

      // Body — use "1" instead of "✓" so cell counts sum up to a
      // meaningful headcount per column (user prefers a sum-able
      // numeric marker).
      const body = [];
      activeEmployees.forEach((emp) => {
        const row = [fixText(emp.name || '')];
        chunkDates.forEach((date) => {
          const assigned = projectsForEmployee(emp.id, date);
          row.push(assigned.length > 0 ? '1' : '');
        });
        body.push(row);
      });

      const columnStyles = { 0: { cellWidth: empColWidth, halign: 'left', fontSize: 6 } };
      for (let i = 1; i <= chunkDates.length; i++) {
        columnStyles[i] = { cellWidth: dayColWidth, halign: 'center', fontSize: 6 };
      }

      autoTable(doc, {
        head, body, startY,
        margin: { left: margin, right: margin, bottom: 10 },
        tableWidth: 'wrap',
        styles: {
          fontSize: 5,
          cellPadding: 0.8,
          lineColor: [180, 180, 180],
          lineWidth: 0.1,
          font: activeFont,
        },
        headStyles: {
          fillColor: PDF_RGB.dayHeader,
          textColor: PDF_RGB.darkGrey,
          fontStyle: 'bold',
          halign: 'center',
          fontSize: 5,
          cellPadding: 1,
        },
        columnStyles,
        alternateRowStyles: { fillColor: PDF_RGB.employee },
        didParseCell(data) {
          if (data.section === 'body') {
            const colIdx = data.column.index;
            if (colIdx > 0) {
              const dateIdx = colIdx - 1;
              if (dateIdx >= 0 && dateIdx < chunkDates.length) {
                const date = chunkDates[dateIdx];
                const isSunday = date.getDay() === 0;
                const isSaturday = date.getDay() === 6;
                const cellContent = data.cell.raw;
                if (cellContent === '1') {
                  data.cell.styles.fillColor = [220, 252, 231]; // green-100
                  data.cell.styles.textColor = [22, 101, 52];    // green-800
                  data.cell.styles.fontStyle = 'bold';
                } else if (isSunday) {
                  data.cell.styles.fillColor = PDF_RGB.sunday;
                } else if (isSaturday) {
                  data.cell.styles.fillColor = PDF_RGB.saturday;
                }
              }
            }
          }
        },
        showHead: 'everyPage',
        // Legend on every page — draw it during the didDrawPage hook
        // so it appears on continuation pages as well as the first.
        didDrawPage() {
          drawLegend(pageHeight - 4);
        },
      });
    }

    // Page numbers on every page — done after all tables are drawn so
    // we know the final total. Matches the Team Availability layout.
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(6);
      doc.setTextColor(...PDF_RGB.lightGrey);
      doc.text(`Page ${i}/${totalPages}`, pageWidth - margin - 12, pageHeight - 3);
    }

    const fileStartStr = format(new Date(startDate), 'yyyy-MM-dd');
    const fileEndStr = format(new Date(endDate), 'yyyy-MM-dd');
    doc.save(`Unified_Overview_${fileStartStr}_to_${fileEndStr}.pdf`);
    return true;
  } catch (error) {
    console.error('Error generating Unified Overview PDF:', error);
    throw error;
  }
};

